import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

function Settings() {
  const [activeTab, setActiveTab] = useState("topics");
  const [topics, setTopics] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTopicId, setEditingTopicId] = useState(null);
  const [editingAgeGroupId, setEditingAgeGroupId] = useState(null);
  const [editingTopicDescription, setEditingTopicDescription] = useState("");
  const [editingAgeGroupDescription, setEditingAgeGroupDescription] =
    useState("");
  const [showDeleteTopicModal, setShowDeleteTopicModal] = useState(false);
  const [showDeleteAgeGroupModal, setShowDeleteAgeGroupModal] = useState(false);
  const [showNewTopicModal, setShowNewTopicModal] = useState(false);
  const [showNewAgeGroupModal, setShowNewAgeGroupModal] = useState(false);
  const [topicToDelete, setTopicToDelete] = useState(null);
  const [ageGroupToDelete, setAgeGroupToDelete] = useState(null);
  const [newTopicDescription, setNewTopicDescription] = useState("");
  const [newAgeGroupDescription, setNewAgeGroupDescription] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch topics
      const topicsRef = collection(db, "units");
      const topicsQuery = query(topicsRef, orderBy("description"));
      const topicsSnapshot = await getDocs(topicsQuery);
      const topicsList = topicsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTopics(topicsList);

      // Fetch age groups
      const ageGroupsRef = collection(db, "age_groups");
      const ageGroupsQuery = query(ageGroupsRef, orderBy("description"));
      const ageGroupsSnapshot = await getDocs(ageGroupsQuery);
      const ageGroupsList = ageGroupsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAgeGroups(ageGroupsList);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error loading settings data");
      setLoading(false);
    }
  };

  // Topic management functions
  const handleNewTopic = () => {
    setNewTopicDescription("");
    setShowNewTopicModal(true);
  };

  const handleSaveTopic = async () => {
    if (!newTopicDescription.trim()) {
      toast.error("Please enter a description");
      return;
    }

    try {
      const topicsRef = collection(db, "units");
      await addDoc(topicsRef, {
        description: newTopicDescription.trim(),
        created_time: new Date(),
      });

      toast.success("Topic added successfully!");
      setNewTopicDescription("");
      setShowNewTopicModal(false);
      fetchData();
    } catch (error) {
      console.error("Error adding topic:", error);
      toast.error("Error adding topic");
    }
  };

  const handleCancelTopic = () => {
    setNewTopicDescription("");
    setShowNewTopicModal(false);
  };

  const handleEditTopic = (topic) => {
    setEditingTopicId(topic.id);
    setEditingTopicDescription(topic.description);
  };

  const handleSaveEditTopic = async () => {
    if (!editingTopicDescription.trim()) {
      toast.error("Please enter a description");
      return;
    }

    try {
      const topicRef = doc(db, "units", editingTopicId);
      await updateDoc(topicRef, {
        description: editingTopicDescription.trim(),
      });

      toast.success("Topic updated successfully!");
      setEditingTopicId(null);
      setEditingTopicDescription("");
      fetchData();
    } catch (error) {
      console.error("Error updating topic:", error);
      toast.error("Error updating topic");
    }
  };

  const handleCancelEditTopic = () => {
    setEditingTopicId(null);
    setEditingTopicDescription("");
  };

  const handleDeleteTopic = (topic) => {
    setTopicToDelete(topic);
    setShowDeleteTopicModal(true);
  };

  const confirmDeleteTopic = async () => {
    try {
      const topicRef = doc(db, "units", topicToDelete.id);
      await deleteDoc(topicRef);

      toast.success("Topic deleted successfully!");
      setShowDeleteTopicModal(false);
      setTopicToDelete(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting topic:", error);
      toast.error("Error deleting topic");
    }
  };

  const cancelDeleteTopic = () => {
    setShowDeleteTopicModal(false);
    setTopicToDelete(null);
  };

  // Age Group management functions
  const handleNewAgeGroup = () => {
    setNewAgeGroupDescription("");
    setShowNewAgeGroupModal(true);
  };

  const handleSaveAgeGroup = async () => {
    if (!newAgeGroupDescription.trim()) {
      toast.error("Please enter a description");
      return;
    }

    try {
      const ageGroupsRef = collection(db, "age_groups");
      await addDoc(ageGroupsRef, {
        description: newAgeGroupDescription.trim(),
        created_time: new Date(),
      });

      toast.success("Age group added successfully!");
      setNewAgeGroupDescription("");
      setShowNewAgeGroupModal(false);
      fetchData();
    } catch (error) {
      console.error("Error adding age group:", error);
      toast.error("Error adding age group");
    }
  };

  const handleCancelAgeGroup = () => {
    setNewAgeGroupDescription("");
    setShowNewAgeGroupModal(false);
  };

  const handleEditAgeGroup = (ageGroup) => {
    setEditingAgeGroupId(ageGroup.id);
    setEditingAgeGroupDescription(ageGroup.description);
  };

  const handleSaveEditAgeGroup = async () => {
    if (!editingAgeGroupDescription.trim()) {
      toast.error("Please enter a description");
      return;
    }

    try {
      const ageGroupRef = doc(db, "age_groups", editingAgeGroupId);
      await updateDoc(ageGroupRef, {
        description: editingAgeGroupDescription.trim(),
      });

      toast.success("Age group updated successfully!");
      setEditingAgeGroupId(null);
      setEditingAgeGroupDescription("");
      fetchData();
    } catch (error) {
      console.error("Error updating age group:", error);
      toast.error("Error updating age group");
    }
  };

  const handleCancelEditAgeGroup = () => {
    setEditingAgeGroupId(null);
    setEditingAgeGroupDescription("");
  };

  const handleDeleteAgeGroup = (ageGroup) => {
    setAgeGroupToDelete(ageGroup);
    setShowDeleteAgeGroupModal(true);
  };

  const confirmDeleteAgeGroup = async () => {
    try {
      const ageGroupRef = doc(db, "age_groups", ageGroupToDelete.id);
      await deleteDoc(ageGroupRef);

      toast.success("Age group deleted successfully!");
      setShowDeleteAgeGroupModal(false);
      setAgeGroupToDelete(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting age group:", error);
      toast.error("Error deleting age group");
    }
  };

  const cancelDeleteAgeGroup = () => {
    setShowDeleteAgeGroupModal(false);
    setAgeGroupToDelete(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <p className="mt-2 text-gray-600">Loading settings...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="py-8 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-primary-600">Settings</h1>
            <p className="text-gray-600 mt-2">Manage topics and age groups</p>
          </div>

          {/* Tabs */}
          <div className="mb-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab("topics")}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "topics"
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Topics
                </button>
                <button
                  onClick={() => setActiveTab("ageGroups")}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "ageGroups"
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Age Groups
                </button>
              </nav>
            </div>
          </div>

          {/* Topics Tab */}
          {activeTab === "topics" && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Topics</h2>
                <button
                  onClick={handleNewTopic}
                  className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors flex items-center space-x-2"
                >
                  <PlusIcon className="w-4 h-4" />
                  <span>New Topic</span>
                </button>
              </div>

              <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {/* Existing Topics */}
                    {topics.map((topic) => (
                      <tr key={topic.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingTopicId === topic.id ? (
                            <input
                              type="text"
                              value={editingTopicDescription}
                              onChange={(e) =>
                                setEditingTopicDescription(e.target.value)
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                              autoFocus
                            />
                          ) : (
                            <div className="text-sm text-gray-900">
                              {topic.description}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            {editingTopicId === topic.id ? (
                              <>
                                <button
                                  onClick={handleSaveEditTopic}
                                  className="text-green-600 hover:text-green-900 transition-colors p-1"
                                  title="Save"
                                >
                                  <CheckIcon className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={handleCancelEditTopic}
                                  className="text-red-600 hover:text-red-900 transition-colors p-1"
                                  title="Cancel"
                                >
                                  <XMarkIcon className="w-5 h-5" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleEditTopic(topic)}
                                  className="text-blue-600 hover:text-blue-900 transition-colors p-1"
                                  title="Edit"
                                >
                                  <PencilIcon className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteTopic(topic)}
                                  className="text-red-600 hover:text-red-900 transition-colors p-1"
                                  title="Delete"
                                >
                                  <TrashIcon className="w-5 h-5" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Age Groups Tab */}
          {activeTab === "ageGroups" && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Age Groups
                </h2>
                <button
                  onClick={handleNewAgeGroup}
                  className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors flex items-center space-x-2"
                >
                  <PlusIcon className="w-4 h-4" />
                  <span>New Age Group</span>
                </button>
              </div>

              <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {/* Existing Age Groups */}
                    {ageGroups.map((ageGroup) => (
                      <tr key={ageGroup.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingAgeGroupId === ageGroup.id ? (
                            <input
                              type="text"
                              value={editingAgeGroupDescription}
                              onChange={(e) =>
                                setEditingAgeGroupDescription(e.target.value)
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                              autoFocus
                            />
                          ) : (
                            <div className="text-sm text-gray-900">
                              {ageGroup.description}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            {editingAgeGroupId === ageGroup.id ? (
                              <>
                                <button
                                  onClick={handleSaveEditAgeGroup}
                                  className="text-green-600 hover:text-green-900 transition-colors p-1"
                                  title="Save"
                                >
                                  <CheckIcon className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={handleCancelEditAgeGroup}
                                  className="text-red-600 hover:text-red-900 transition-colors p-1"
                                  title="Cancel"
                                >
                                  <XMarkIcon className="w-5 h-5" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleEditAgeGroup(ageGroup)}
                                  className="text-blue-600 hover:text-blue-900 transition-colors p-1"
                                  title="Edit"
                                >
                                  <PencilIcon className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteAgeGroup(ageGroup)}
                                  className="text-red-600 hover:text-red-900 transition-colors p-1"
                                  title="Delete"
                                >
                                  <TrashIcon className="w-5 h-5" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* New Topic Modal */}
          {showNewTopicModal && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                <div className="mt-3">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Add New Topic
                  </h3>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <input
                      type="text"
                      value={newTopicDescription}
                      onChange={(e) => setNewTopicDescription(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter topic description"
                      autoFocus
                    />
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={handleCancelTopic}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveTopic}
                      className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* New Age Group Modal */}
          {showNewAgeGroupModal && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                <div className="mt-3">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Add New Age Group
                  </h3>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <input
                      type="text"
                      value={newAgeGroupDescription}
                      onChange={(e) =>
                        setNewAgeGroupDescription(e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter age group description"
                      autoFocus
                    />
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={handleCancelAgeGroup}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveAgeGroup}
                      className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Delete Topic Modal */}
          {showDeleteTopicModal && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                <div className="mt-3 text-center">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Delete Topic
                  </h3>
                  <p className="text-sm text-gray-500 mb-6">
                    Are you sure you want to delete "
                    {topicToDelete?.description}"? This action cannot be undone.
                  </p>
                  <div className="flex justify-center space-x-4">
                    <button
                      onClick={confirmDeleteTopic}
                      className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
                    >
                      Delete
                    </button>
                    <button
                      onClick={cancelDeleteTopic}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Delete Age Group Modal */}
          {showDeleteAgeGroupModal && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                <div className="mt-3 text-center">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Delete Age Group
                  </h3>
                  <p className="text-sm text-gray-500 mb-6">
                    Are you sure you want to delete "
                    {ageGroupToDelete?.description}"? This action cannot be
                    undone.
                  </p>
                  <div className="flex justify-center space-x-4">
                    <button
                      onClick={confirmDeleteAgeGroup}
                      className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
                    >
                      Delete
                    </button>
                    <button
                      onClick={cancelDeleteAgeGroup}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Settings;
